import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { IonButton, IonIcon } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { closeOutline, megaphoneOutline } from 'ionicons/icons';

import { EngagementHubService } from '../../core/services/engagement-hub.service';
import { MobileHapticsService } from '../../core/native/mobile-haptics.service';
import {
  navigateToSessionRoom,
  navigateToStageRoom
} from '../../core/session-navigation';
import type { BroadcastAction, BroadcastMessage } from '../../core/models/broadcast.models';

/**
 * Mobile in-app broadcast toast — the attendee-facing half of the admin
 * broadcast feature already shipped on web.
 *
 * Event admins compose a broadcast on the web event-dashboard; the
 * Engagement service fans `ReceiveBroadcast` to the `Event_{eventId}`
 * group on `/hubs/engagement`. The mobile `EngagementHubService` is
 * already a member of that group (JoinEventGroup), surfaces the payload
 * on `broadcasts$`, and this component renders it as a non-intrusive
 * toast stacked at the top of the viewport — mirroring the web
 * `BroadcastToastComponent`.
 *
 * Mounted once in the root `<App>` template so a broadcast can fly in
 * over any tab/page. Multiple broadcasts queue; each auto-dismisses
 * after its server-provided `displayDuration` (default 60s) or can be
 * dismissed manually. Action buttons deep-link into the app or open
 * external links.
 */
@Component({
  selector: 'mob-broadcast-toast',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IonButton, IonIcon],
  template: `
    @if (broadcasts().length) {
      <div class="mob-bcast" role="region" aria-label="Event announcements">
        @for (msg of broadcasts(); track msg.id) {
          <article class="mob-bcast__card" role="alert">
            <div class="mob-bcast__head">
              <span class="mob-bcast__badge" aria-hidden="true">
                <ion-icon name="megaphone-outline" />
              </span>
              <h2 class="mob-bcast__title">{{ msg.title }}</h2>
              <button
                type="button"
                class="mob-bcast__close"
                aria-label="Dismiss announcement"
                (click)="dismiss(msg.id)"
              >
                <ion-icon name="close-outline" />
              </button>
            </div>
            <p class="mob-bcast__body">{{ msg.body }}</p>
            @if (msg.actions.length) {
              <div class="mob-bcast__actions">
                @for (action of msg.actions; track action.label) {
                  <ion-button
                    size="small"
                    shape="round"
                    fill="solid"
                    (click)="handleAction(action, msg.id)"
                  >
                    {{ action.label }}
                  </ion-button>
                }
              </div>
            }
          </article>
        }
      </div>
    }
  `,
  styles: [
    `
      .mob-bcast {
        position: fixed;
        top: calc(env(safe-area-inset-top, 0) + 0.5rem);
        left: 0;
        right: 0;
        z-index: 2000;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.5rem;
        padding: 0 0.75rem;
        pointer-events: none;
      }
      .mob-bcast__card {
        pointer-events: auto;
        width: 100%;
        max-width: 32rem;
        background: var(--vevents-color-bg, #ffffff);
        color: var(--vevents-color-text, #0f172a);
        border-radius: 16px;
        padding: 0.85rem 0.95rem 0.95rem;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.22);
        border: 1px solid rgba(var(--ion-color-primary-rgb, 56, 189, 248), 0.25);
        animation: mob-bcast-in 0.24s ease-out;
      }
      .mob-bcast__head {
        display: flex;
        align-items: center;
        gap: 0.55rem;
      }
      .mob-bcast__badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 auto;
        width: 30px;
        height: 30px;
        border-radius: 50%;
        background: rgba(var(--ion-color-primary-rgb, 56, 189, 248), 0.16);
        color: var(--vevents-color-primary, #38bdf8);
        font-size: 1.05rem;
      }
      .mob-bcast__title {
        flex: 1 1 auto;
        margin: 0;
        font-size: 1rem;
        font-weight: 600;
        line-height: 1.25;
      }
      .mob-bcast__close {
        flex: 0 0 auto;
        background: transparent;
        border: 0;
        padding: 0.2rem;
        margin: -0.2rem -0.2rem 0 0;
        color: inherit;
        opacity: 0.6;
        font-size: 1.25rem;
        line-height: 1;
        cursor: pointer;
      }
      .mob-bcast__body {
        margin: 0.5rem 0 0;
        font-size: 0.92rem;
        line-height: 1.4;
        opacity: 0.85;
        white-space: pre-wrap;
      }
      .mob-bcast__actions {
        display: flex;
        flex-wrap: wrap;
        gap: 0.4rem;
        margin-top: 0.7rem;
      }
      @keyframes mob-bcast-in {
        from {
          transform: translateY(-16px);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }
    `
  ]
})
export class BroadcastToastComponent {
  private readonly hub = inject(EngagementHubService);
  private readonly router = inject(Router);
  private readonly haptics = inject(MobileHapticsService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly broadcasts = signal<BroadcastMessage[]>([]);

  /** Default auto-dismiss window when the server omits `displayDuration`. */
  private static readonly DEFAULT_DURATION_SEC = 60;
  /** Cap the on-screen queue so a flood of broadcasts can't bury the UI. */
  private static readonly MAX_VISIBLE = 4;

  private readonly timers = new Map<string, ReturnType<typeof setTimeout>>();

  constructor() {
    addIcons({ 'megaphone-outline': megaphoneOutline, 'close-outline': closeOutline });

    this.hub.broadcasts$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((msg) => this.show(msg));

    this.destroyRef.onDestroy(() => {
      for (const timer of this.timers.values()) clearTimeout(timer);
      this.timers.clear();
    });
  }

  private show(msg: BroadcastMessage): void {
    // Ignore a duplicate re-delivery of the same broadcast id.
    if (this.broadcasts().some((m) => m.id === msg.id)) return;

    this.broadcasts.update((list) => {
      const next = [...list, msg];
      // Drop the oldest if we exceed the visible cap.
      while (next.length > BroadcastToastComponent.MAX_VISIBLE) {
        const dropped = next.shift();
        if (dropped) this.clearTimer(dropped.id);
      }
      return next;
    });

    // A broadcast is a notification — buzz once (respects the user's
    // haptics preference; no-ops on unsupported platforms).
    void this.haptics.notify('success');

    const durationMs =
      (msg.displayDuration ?? BroadcastToastComponent.DEFAULT_DURATION_SEC) * 1000;
    this.clearTimer(msg.id);
    this.timers.set(
      msg.id,
      setTimeout(() => this.dismiss(msg.id), durationMs)
    );
  }

  protected dismiss(id: string): void {
    this.clearTimer(id);
    this.broadcasts.update((list) => list.filter((m) => m.id !== id));
  }

  /**
   * Route a tapped broadcast action to its mobile destination.
   *
   * Payload semantics mirror the web composer
   * (`apps/web/.../broadcasts/broadcasts.component.ts`) and the server
   * `BroadcastActionType` enum:
   *  - `ExternalLink`   — payload is an https URL → system/Capacitor browser.
   *  - `DeepLinkRoom`   — payload is a catalogue **room** id. On mobile a
   *                       room is the Stage/Auditorium surface, NOT a
   *                       session — route via `navigateToStageRoom`
   *                       (`/tabs/booths/{roomId}?roomId=`).
   *  - `DeepLinkSession`— payload is a **session** id → full-screen
   *                       Session Room (`/session/{sessionId}`).
   *  - `OpenQuiz`       — payload is a quiz id → native assessment taker.
   *  - `DownloadFile`   — payload is a file URL → open externally so the
   *                       OS/browser handles the download.
   *  - `OpenProfile`    — payload is the constant `'profile'` (web opens the
   *                       attendee's own profile editor) → the Me tab.
   */
  protected handleAction(action: BroadcastAction, broadcastId: string): void {
    switch (action.actionType) {
      case 'ExternalLink':
      case 'DownloadFile':
        void this.openExternal(action.payload);
        this.dismiss(broadcastId);
        break;

      case 'DeepLinkRoom': {
        const roomId = this.parseId(action.payload);
        if (roomId != null) {
          void navigateToStageRoom(this.router, roomId);
        }
        this.dismiss(broadcastId);
        break;
      }

      case 'DeepLinkSession': {
        const sessionId = this.parseId(action.payload);
        if (sessionId != null) {
          void navigateToSessionRoom(this.router, sessionId);
        }
        this.dismiss(broadcastId);
        break;
      }

      case 'OpenQuiz': {
        // Open the native assessment taker. The payload is the quiz id.
        const quizId = this.parseId(action.payload);
        if (quizId != null) {
          void this.router.navigate(['/assessment', quizId]);
        }
        this.dismiss(broadcastId);
        break;
      }

      case 'OpenProfile':
        void this.router.navigate(['/tabs/me']);
        this.dismiss(broadcastId);
        break;

      default:
        this.dismiss(broadcastId);
    }
  }

  /**
   * Coerce a broadcast payload into a positive integer id. Room / session /
   * quiz payloads arrive as numeric strings; reject blanks and non-numbers
   * so a malformed broadcast can't push a `/NaN` route.
   */
  private parseId(payload: string | undefined | null): number | null {
    if (!payload) return null;
    const id = Number(payload.trim());
    return Number.isInteger(id) && id > 0 ? id : null;
  }

  private clearTimer(id: string): void {
    const timer = this.timers.get(id);
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(id);
    }
  }

  /** Open an external URL via the Capacitor browser, falling back to the tab. */
  private async openExternal(url: string): Promise<void> {
    if (!url) return;
    try {
      const mod = (await import(/* @vite-ignore */ '@capacitor/browser')) as {
        Browser?: { open: (opts: { url: string }) => Promise<void> };
      };
      if (mod.Browser) {
        await mod.Browser.open({ url });
        return;
      }
    } catch {
      // Fall through to the web fallback below.
    }
    if (typeof window !== 'undefined') {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }
}
